import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useSettingsStore } from '@/stores/settings.js'
import { useConsentStore } from '@/stores/consent'
import { NOINDEX_FOLLOW } from '@/utils/seoRobots'

const noindex = { robots: NOINDEX_FOLLOW }

const routes = [{
  path: '/intro',
  name: 'Intro',
  component: () => import('@/views/IntroView.vue'),
  meta: { guestOnly: true, hideHeaderFooter: true, ...noindex }
}, {
  path: '/intro-animation',
  name: 'IntroAnimation',
  component: () => import('@/views/IntroAnimationView.vue'),
  meta: { guestOnly: true, hideHeaderFooter: true, ...noindex }
}, {
  path: '/landing', name: 'Landing', component: () => import('@/views/LandingPage.vue'), meta: { ...noindex }
}, {
  path: '/', name: 'Home', component: () => import('@/views/HomeView.vue')
}, {
  path: '/recipes', name: 'Recipes', component: () => import('@/views/RecipesView.vue')
}, {
  path: '/recipe/:slug', name: 'RecipeDetails', component: () => import('@/views/RecipeDetails.vue')
}, {
  path: '/planner', name: 'Planner', component: () => import('@/views/PlannerView.vue'), meta: { hideBottomNav: true, hideLoginButton: true, ...noindex }
}, {
  path: '/groceries', name: 'Groceries', component: () => import('@/views/GroceriesView.vue'), meta: { ...noindex }
}, {
  path: '/settings', name: 'Settings', component: () => import('@/views/SettingsView.vue'), meta: { ...noindex }
}, {
  path: '/cookie-preferences', name: 'CookiePreferences', component: () => import('@/views/CookiePreferencesView.vue'), meta: { ...noindex }
}, {
  path: '/manage-notifications',
  name: 'ManageNotifications',
  component: () => import('@/views/ManageNotificationsView.vue'),
  meta: { requiresAuth: true, ...noindex }
}, {
  path: '/login', name: 'Login', component: () => import('@/views/auth/LoginView.vue'), meta: { guestOnly: true, ...noindex }
}, {
  path: '/auth/google/callback',
  name: 'GoogleCallback',
  component: () => import('@/views/auth/GoogleCallbackView.vue'),
  meta: {
    guest: true,
    ...noindex,
  },
}, {
  path: '/register',
  name: 'Register',
  component: () => import('@/views/auth/RegisterView.vue'),
  meta: { guestOnly: true, ...noindex }
}, {
  path: '/forgot-password', name: 'ForgotPassword', component: () => import('@/views/auth/ForgotPassowrdView.vue'), meta: { ...noindex }
}, {
  path: '/reset-password', name: 'ResetPassword', component: () => import('@/views/auth/ResetPasswordView.vue'), meta: { ...noindex }
}, {
  path: '/verify-email', name: 'VerifyEmail', component: () => import('@/views/auth/VerifyEmailView.vue'), meta: { ...noindex }
}, {
  path: '/email-verification-pending',
  name: 'EmailVerificationPending',
  component: () => import('@/views/auth/EmailVerificationPendingView.vue'),
  meta: { requiresAuth: true, ...noindex },
}, {
  path: '/registration-confirmation',
  name: 'RegistrationConfirmation',
  component: () => import('@/views/auth/RegistrationConfirmationView.vue'),
  meta: { ...noindex }
}, {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/user/ProfileView.vue'),
    meta: { requiresAuth: true, ...noindex },
  }, {
    path: '/delete-account',
    name: 'DeleteAccount',
    component: () => import('@/views/user/DeleteAccountView.vue'),
    meta: { requiresAuth: true, ...noindex },
  }, {
  path: '/family-group',
  name: 'FamilyGroup',
  component: () => import('@/views/GroupsView.vue'),
  meta: { requiresAuth: true, ...noindex },
}, {
  path: '/join-group/:token',
  name: 'JoinGroup',
  component: () => import('@/views/JoinGroupView.vue'),
  props: true,
  meta: { requiresAuth: true, ...noindex },
}, {
  path: '/content/:slug', name: 'content-page', component: () => import('@/views/ContentPage.vue'), props: true
}, {
  path: '/free-meal-plan',
  redirect: (to) => ({ path: '/ai-diet-planner', query: to.query, hash: to.hash }),
}, {
  path: '/ai-diet-planner',
  name: 'FreeMealPlan',
  component: () => import('@/views/FreeMealPlan.vue'),
  meta: { hideBottomNav: true, hideLoginButton: true }
}, {
  // Paid-search entry page. It sells, then hands off to /ai-diet-planner, which
  // stays the single wizard for everyone who arrives already committed.
  // `noindex` for now: /ai-diet-planner is already indexed for overlapping
  // meal-planner intent in twelve locales, so this stays campaign-only until it
  // has earned its own listing.
  path: '/weekmenu-maken',
  name: 'CampaignLandingNl',
  component: () => import('@/views/CampaignLandingView.vue'),
  meta: { guestOnly: true, hideBottomNav: true, suppressAuthAutoOpen: true, consentUnobtrusive: true, ...noindex }
}, {
  path: '/unsubscribe',
  name: 'Unsubscribe',
  component: () => import('@/views/UnsubscribeView.vue'),
  meta: { ...noindex }
}, {
  path: '/payment/success',
  name: 'PaymentSuccess',
  component: () => import('@/views/PaymentSuccessView.vue'),
  meta: { ...noindex }
}, {
  path: '/upgrade',
  name: 'Upgrade',
  component: () => import('@/views/UpgradeView.vue'),
  meta: { ...noindex }
},

  {
    path: '/admin/pages',
    name: 'admin-pages',
    component: () => import('@/views/admin/AdminPageList.vue'),
    meta: { requiresAdmin: true, ...noindex }
  }, {
    path: '/admin/pages/new',
    name: 'admin-pages-new',
    component: () => import('@/views/admin/AdminPageForm.vue'),
    meta: { requiresAdmin: true, ...noindex }
  }, {
    path: '/admin/pages/:slug',
    name: 'admin-pages-edit',
    component: () => import('@/views/admin/AdminPageForm.vue'),
    props: true,
    meta: { requiresAdmin: true, ...noindex }
  }, {
    path: '/admin/analytics-events',
    name: 'admin-analytics-events',
    component: () => import('@/views/admin/AdminAnalyticsEventsView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, ...noindex }
  },

  // Always leave this as last one
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue'), meta: { ...noindex } }]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const settings = useSettingsStore()
  const recipeQueryKeys = new Set([
    'view',
    'page',
    'search',
    'mealType',
    'country',
    'dietary',
    'cost',
    'difficulty',
    'prepTime',
    'favorites',
    'tag'
  ])

  // If we have a token but no user, try to fetch the user
  if (auth.token && !auth.user) {
    try {
      await auth.fetchUser()
    } catch {
      auth.logout()
    }
  }

  // Check if running in Capacitor (mobile)
  const isMobileApp = window.Capacitor && window.Capacitor.isNativePlatform()

  // For mobile app, redirect to animated intro if not seen yet, except for auth-related ones
  if (isMobileApp && !settings.introSeen && !auth.user && !['Intro', 'IntroAnimation', 'Login', 'Register', 'ForgotPassword', 'ResetPassword', 'VerifyEmail', 'GoogleCallback', 'EmailVerificationPending', 'RegistrationConfirmation', 'FreeMealPlan', 'Unsubscribe', 'Landing', 'PaymentSuccess', 'Upgrade'].includes(to.name)) {
    // Save the original URL in the auth store
    auth.saveOriginalUrl(to.fullPath)
    return { name: 'IntroAnimation' }
  }

  // Backward compatibility: old recipe filters on "/" now belong to "/recipes".
  if (to.name === 'Home') {
    const hasRecipeQuery = Object.keys(to.query || {}).some((key) => recipeQueryKeys.has(key))
    if (hasRecipeQuery) {
      const nextQuery = { ...to.query }
      delete nextQuery.view
      return { name: 'Recipes', query: nextQuery }
    }
  }

  // Planner is for signed-in users; guests use the public free meal plan flow
  if (to.name === 'Planner' && !auth.user) {
    return { name: 'FreeMealPlan', query: to.query }
  }

  // Redirect to login if the route requires authentication and the user is not logged in
  if (to.meta.requiresAuth && !auth.user) {
    // Save the original URL in the auth store
    auth.saveOriginalUrl(to.fullPath)
    return { name: 'Login', query: { redirect: to.fullPath } }
  }

  // Redirect to not-found if the route requires admin access and the user is not an admin
  if (to.meta.requiresAdmin && !auth.user?.is_admin) {
    return { name: 'not-found' }
  }

  // Redirect to home if the route is for guests only and the user is logged in
  // To make a route guest-only, add meta: { guestOnly: true } to the route definition
  if (to.meta.guestOnly && auth.user) {
    return { name: 'Home' }
  }
})

router.afterEach((to) => {
  // Ensure we scroll to top on navigation.
  // This is a safety measure in addition to scrollBehavior,
  // especially useful if #app is the scroll container.
  window.scrollTo({ top: 0 })
  const appElement = document.getElementById('app')
  if (appElement) {
    appElement.scrollTo({ top: 0})
  }

  const consentStore = useConsentStore()
  if (consentStore.consent.analytics) {
    try {
      const now = Date.now()
      const visitKey = 'yumnia_last_visit_at'
      const lastVisitAt = Number(localStorage.getItem(visitKey) || 0)
      const thirtyMinutesMs = 30 * 60 * 1000
      if (lastVisitAt && now - lastVisitAt > thirtyMinutesMs) {
        consentStore.trackEvent('return_visit', {
          page_path: to.fullPath,
          minutes_since_last_visit: Math.floor((now - lastVisitAt) / 60000),
        })
      }
      localStorage.setItem(visitKey, String(now))
    } catch {
      /* ignore */
    }

    consentStore.trackPageView(to)
  }
})

export default router
